/* eslint-disable eqeqeq */
/* eslint-disable react-hooks/exhaustive-deps */
import { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import { Button, Icon, Loader, Menu, Tab, Modal } from 'semantic-ui-react';
import Joyride from 'react-joyride';

import { CustomSegment, EmptyContainer, IssuesBox } from '../../components';
import { PermissionDenied } from '../../components/errorStatus';

import {
  ACTIVITY_TAB_KEYS,
  ACTIVITY_TABS,
  APP_NAME,
  PARENT_ACTIVITY_TABS,
  SIDEBAR_TYPES,
} from '../../utils/constant';

import {
  cloneDeep,
  compact,
  find,
  get,
  isEmpty,
  isString,
  map,
  remove,
  size,
} from '../../utils/lodash';

import {
  clearCurrentActivity,
  getAllActivityData,
  putActivityStatus,
  setActivityState,
  setSidebarType,
  setUserOnboardingOption,
  setVisibleTips,
} from '../../store/actions';

import {
  getActivityType,
  getEvidenceTitle,
  getActivityCompletionIssues,
  getPageDisable,
  isTabDeactivated,
} from '../../utils';

import {
  ActivitiesContainer,
  ActivityEvidence,
  ActivityGoal,
  ActivityGoalsOverview,
  ActivityObjective,
  ActivityParentEvidence,
  ActivityParentObjective,
  ActivityParentTheory,
  ActivityTheory,
  ActualOutcomes,
  ActualOutput,
  CustomItem,
  GreenLightComponent,
  MainEvalPlan,
  Notes,
  ParentReportCharts,
  ReportActivity,
  TheoryOfChange,
} from './form';

import { BeaconComponent, Tooltip } from '../../components';
import Documentation from './form/documentation';
import ActivityPlan from './form/activityPlan';
import { EvaluationPlanSection } from './form/evaluation/evaluationPlanSection';
import { OutcomeSummarySections } from './form/evaluation/outcomeSummarySections';
import EvaluationMethodology from './form/evaluation/EvaluationMethodology';
import { getGrantTrialDefinedTexts } from '../../utils/pageText';
import { isParentProgramActivity } from '../../utils/activity';
import { checkPermissionDenied } from '../../utils/activity/userPermission';

import './activities.scss';

const steps = [
  {
    target: '.activity-goal',
    content: <Trans i18nKey="activity.activity_steps.joyride_goal_tip" />,
    placement: 'top',
    disableBeacon: true,
  },
  {
    target: '.ui.pointing.secondary.menu',
    content: (
      <Trans
        i18nKey="activity.activity_steps.joyride_steps_tip"
        components={{ b: <b /> }}
      />
    ),
    placement: 'bottom',
  },
  {
    target: '.activity-report',
    content: (
      <Trans
        i18nKey="activity.activity_steps.joyride_report_tip"
        components={{ b: <b /> }}
      />
    ),
    placement: 'bottom-end',
  },
];

const ActivitySteps = (props) => {
  const { t } = useTranslation();
  const { match } = props;
  const [state, setState] = useState({
    panes: [],
    currentTab: 0,
    disabled: ['project.edit.documentation', 'project.edit.report'],
    approvalMethod: 'allowSelf',
    authorisationCode: null,
    approvalView: false,
    confirmModal: false,
    single: false,
    reportAllImagesLoaded: true,
    isEvaluationPageOpen: false,
    infoSteps: false,
  });

  const dispatch = useDispatch();
  const selectors = useSelector((state) => state);
  const {
    activity,
    authentication: { user },
    extra: { evidenceTypes, helpTips, festivalTypes },
    sidebar: { isVisibleTips: visibleTips },
  } = selectors;
  const navigate = useNavigate();
  const { currentActivity } = activity;
  props = { ...props, ...activity, user };

  /****** bhu:debug
  console.warn(`CURRACT ${currentActivity.name}`);
  console.dir(currentActivity, {depth: 3});
  *******/

  const currencyType = get(currentActivity, 'currencyType');

  // ── Cross-tab sync: receive Report Builder saves into Redux ─────────────────
  // The individual step components (Documentation, Evaluation Methodology,
  // per-outcome evaluation narrative) used to each run their own
  // BroadcastChannel('takso_activity') listener with local override/version
  // state, because putActivity/saveEvaluationNarrative etc. only PUT to the
  // backend — they never update Redux. That per-field approach silently broke
  // whenever the listening component wasn't mounted: this app's step Tab
  // renders only the active pane (renderActiveOnly, Semantic UI's default),
  // so switching steps unmounts the previous step's listener entirely,
  // meaning a save from Report Builder while on a different step was never
  // received. ActivitySteps itself stays mounted for the whole time the user
  // is viewing this activity, so listening here once and merging straight
  // into Redux (via setActivityState, local-only — no extra PUT) fixes it
  // regardless of which step is active when the broadcast arrives, and lets
  // each field's own component go back to just reading currentActivity.
  //
  // currentActivityRef mirrors currentActivity on every render so the
  // message handler always merges against the latest evaluationPlans
  // (relevant for evaluationNarrative below) without needing to tear down
  // and recreate the channel on every activity field change.
  const currentActivityRef = useRef(currentActivity);
  useEffect(() => {
    currentActivityRef.current = currentActivity;
  }, [currentActivity]);

  useEffect(() => {
    if (!currentActivity?.id) return;
    const ch = new BroadcastChannel('takso_activity');
    ch.onmessage = (e) => {
      const data = e.data;
      if (!data || data.activityId !== currentActivity.id) return;
      switch (data.type) {
        case 'summary':
          dispatch(setActivityState({ summary: data.summary }));
          break;
        case 'analysis':
          dispatch(setActivityState({ analysis: data.analysis }));
          break;
        case 'result':
          dispatch(setActivityState({ result: data.result }));
          break;
        case 'reflection':
          dispatch(setActivityState({ reflection: data.reflection }));
          break;
        case 'nextSteps':
          dispatch(setActivityState({ nextSteps: data.nextSteps }));
          break;
        case 'methodology':
          dispatch(
            setActivityState({
              evaluationMethodology: data.evaluationMethodology,
            })
          );
          break;
        case 'financialNote':
          dispatch(setActivityState({ financialNote: data.financialNote }));
          break;
        case 'reportInclusions':
          dispatch(
            setActivityState({
              reportInclusions: JSON.stringify(data.reportInclusions),
            })
          );
          break;
        case 'evaluationNarrative': {
          const plans = cloneDeep(
            currentActivityRef.current?.evaluationPlans || []
          );
          const question = plans
            .flatMap((plan) => plan.evaluationQuestions || [])
            .find((q) => q.id === data.evaluationId);
          if (question) {
            question.reportNarrative = data.reportNarrative;
            dispatch(setActivityState({ evaluationPlans: plans }));
          }
          break;
        }
        default:
          break;
      }
    };
    return () => ch.close();
  }, [currentActivity?.id, dispatch]);

  // Report tab scroll memory
  const REPORT_TAB_ID = 7;
  const reportScrollPos = useRef(0); // saved scroll position
  const reportVisited = useRef(false); // has the Report tab been opened yet?
  const prevTab = useRef(state.currentTab);

  useEffect(() => {
    const container = document.querySelector('.act-content-layer');
    if (!container) return;

    const leavingReport = prevTab.current === REPORT_TAB_ID;
    const enteringReport = state.currentTab === REPORT_TAB_ID;

    if (leavingReport && !enteringReport) {
      // Save scroll position when navigating away from Report tab
      reportScrollPos.current = container.scrollTop;
    }

    if (enteringReport) {
      if (!reportVisited.current) {
        // First visit — go to top
        container.scrollTop = 0;
        reportVisited.current = true;
      } else {
        // Returning — restore saved position
        container.scrollTop = reportScrollPos.current;
      }
    }

    prevTab.current = state.currentTab;
  }, [state.currentTab]);

  useEffect(() => {
    const fetchData = async () => {
      document.title = `${t('activity.activity_steps.document_title', { app_name: APP_NAME })}`;
      try {
        dispatch(clearCurrentActivity());

        const _approvalMethod =
          currentActivity.approvalMethod === 1 ? 'ppmsCode' : 'allowSelf';
        const _authorisationCode = currentActivity.codeLabel
          ? currentActivity.codeLabel
          : null;

        setState((prevState) => ({
          ...prevState,
          approvalMethod: _approvalMethod,
          authorisationCode: _authorisationCode,
        }));

        if (_authorisationCode || _approvalMethod !== 'allowSelf') {
          setState((prevState) => ({
            ...prevState,
            approvalView: true,
          }));
        }

        if (match.params) {
          dispatch(getAllActivityData(match.params.id));
        }

        window.scrollTo(0, 0);

        if (!user.onboardingOption.activityFormTips) {
          setState((prevState) => ({
            ...prevState,
            infoSteps: true,
          }));
        }
      } catch (error) {
        console.error('ActivitySteps -> useEffect -> error', error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (visibleTips) {
      setState((prevState) => ({
        ...prevState,
        infoSteps: visibleTips,
      }));
      dispatch(setSidebarType(SIDEBAR_TYPES[0], false));
      dispatch(setVisibleTips(false));
    }
  }, [visibleTips]);

  useEffect(() => {
    if (!isEmpty(currentActivity) && match.params.type) {
      const tab = currentActivity
        ? find(
            isParentProgramActivity(currentActivity.activityPlanTypeId)
              ? PARENT_ACTIVITY_TABS(t)
              : ACTIVITY_TABS(t),
            (o) => o.route === match.params.type
          )
        : null;

      // currentTab
      if (tab.id !== currentTab) {
        changeRoute(match.params.type);
      }
    }
  }, [currentActivity, match.params.type]);

  async function changeRoute(value) {
    try {
      const panes = generateTabs();

      const tab = currentActivity
        ? find(
            isParentProgramActivity(currentActivity.activityPlanTypeId)
              ? PARENT_ACTIVITY_TABS(t)
              : ACTIVITY_TABS(t),
            (o) => {
              return o.id === value || o.route === value;
            }
          )
        : null;

      if (!tab) return;

      const _isTabDeactivated = isTabDeactivated(
        'CURRENT',
        currentActivity,
        panes,
        tab.id
      );

      if (_isTabDeactivated) {
        setState((prevState) => ({
          ...prevState,

          currentTab: 0,
        }));

        // Skip re-navigating when the URL already points here — otherwise a
        // sync triggered by an external URL change (rather than a tab click)
        // pushes a second, identical history entry, and one browser-back
        // press from a jump-link lands back on the same tab instead of
        // returning the user to where they came from.
        if (match.params.type !== 'goal') {
          navigate(`/activities/${match.params.id}/goal`);
        }
      } else {
        setState((prevState) => ({
          ...prevState,

          currentTab: tab.id,
        }));
        if (match.params.type !== tab.route) {
          navigate(`/activities/${match.params.id}/${tab.route}`);
        }
      }
    } catch (error) {
      return false;
    }
  }

  const getAllImages = async (value) => {
    return new Promise((resolve, reject) => {
      try {
        let getAllGraphImages = value['getAllGraphImages'];
        let reportLoaded = value['reportLoaded'];
        let reportAllImagesLoadedLocal = false;

        if (getAllGraphImages || reportLoaded) {
          reportAllImagesLoadedLocal = true;
        } else {
          reportAllImagesLoadedLocal = false;
        }

        setState((prevState) => ({
          ...prevState,
          reportAllImagesLoaded: reportAllImagesLoadedLocal,
        }));
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  };

  function generateTabs() {
    try {
      let completionProgress = null;
      let statusId = null;
      // const {
      //   user,
      //   currentActivity,
      //   getReports,
      //   getReportsLoading,
      //   festivalTypes,
      // } = props;

      const { reportAllImagesLoaded } = state;

      const member = find(currentActivity.members, (member) => {
        return Number(member.userId) === Number(user.id);
      });

      // const primaryPolicy = currentActivity.primaryPolicy
      //   ? currentActivity.primaryPolicy.policyName
      //   : '';
      // const secondaryPolicy = currentActivity.secondaryPolicy
      //   ? currentActivity.secondaryPolicy.policyName
      //   : '';

      if (currentActivity && currentActivity.completionProgress) {
        completionProgress = currentActivity.completionProgress;
        statusId = currentActivity.statusId;
      }

      // const [evaluation1, evaluation2] = currentActivity.evaluation;
      let validationOutput = null;

      try {
        // TODO: Add new validations to evaluation plan
        validationOutput = getActivityCompletionIssues(currentActivity);
      } catch (error) {
        // console.error(error);
      }

      const hasObjectives = isParentProgramActivity(
        currentActivity.activityPlanTypeId
      )
        ? currentActivity.parentOutcomes &&
          currentActivity.parentOutcomes.length > 0
        : currentActivity.objective && currentActivity.objective.length > 0;

      let panes = [
        {
          menuItem: (
            <Menu.Item
              /*
               * Note:  activity-goal class name used for help tip showing class name (don't change it or remove it)
               */
              className="tab-item activity-goal"
              key="project.edit.start"
              disabled={state.isEvaluationPageOpen}
            >
              <CustomItem
                currentTab={state.currentTab}
                activityPlanTypeId={currentActivity.activityPlanTypeId}
                statusList={completionProgress}
                statusId={statusId}
                hasObjectives={hasObjectives}
                activity_step_id="Goal"
                title={t('common.goal').toLocaleUpperCase()}
                popupState={get(props, 'user.options.tips')}
                linkedActivities={currentActivity.linkedActivities}
                popupContentObject={{
                  data: helpTips,
                  key: '10',
                }}
                currentActivity={currentActivity}
              />
            </Menu.Item>
          ),
          render: () => (
            <Tab.Pane
              attached={false}
              className="custom-tab-act"
              loading={activity.actGetLoading}
            >
              <ActivitiesContainer
                next={() => nextTab()}
                previous={() => previousTab()}
                currentTab={state.currentTab}
                tabKey={ACTIVITY_TAB_KEYS.GOAL}
                title={t('common.goal').toLocaleUpperCase()}
                children={
                  <CustomSegment
                    className="organization-goal-to-be-addressed"
                    title={getGrantTrialDefinedTexts(
                      get(currentActivity, 'parentId', null),
                      t(
                        'activity.activity_steps.organisation_goal_to_be_addressed'
                      ),
                      1
                    )}
                    rightHeader={
                      <Loader
                        active={
                          activity.actPut.goal ? activity.actPut.goal : false
                        }
                        inline
                      />
                    }
                    children={
                      <ActivityGoal currentState="project.edit.start" />
                    }
                  />
                }
              />
            </Tab.Pane>
          ),
        },
        {
          menuItem: (
            <Menu.Item
              key="project.edit.objective"
              className="tab-item"
              disabled={state.isEvaluationPageOpen}
            >
              <CustomItem
                currentTab={state.currentTab}
                activityPlanTypeId={currentActivity.activityPlanTypeId}
                isLinkedActivity={currentActivity.isLinkedActivity}
                statusList={completionProgress}
                statusId={statusId}
                activity_step_id="Objective"
                title={t('common.objective').toLocaleUpperCase()}
                popupState={get(user, 'options.tips')}
                linkedActivities={currentActivity.linkedActivities}
                popupContentObject={{
                  data: helpTips,
                  key: '11',
                }}
                currentActivity={currentActivity}
              />
            </Menu.Item>
          ),
          render: () => (
            <Tab.Pane
              attached={false}
              className="custom-tab-act"
              loading={selectors.actGetLoading}
            >
              <ActivitiesContainer
                next={() => nextTab()}
                previous={() => previousTab()}
                currentTab={state.currentTab}
                tabKey={ACTIVITY_TAB_KEYS.OBJECTIVE}
                title={t('common.objective').toLocaleUpperCase()}
                children={
                  <CustomSegment
                    className="set-objective"
                    title={t('activity.activity_steps.set_objective_title')}
                    children={
                      isParentProgramActivity(
                        currentActivity.activityPlanTypeId
                      ) ? (
                        <ActivityParentObjective
                          statusId={statusId}
                          currentState="project.edit.objective"
                        />
                      ) : (
                        <ActivityObjective
                          statusId={statusId}
                          currentState="project.edit.objective"
                        />
                      )
                    }
                    rightHeader={
                      <Loader
                        active={
                          activity.actPut.objective
                            ? activity.actPut.objective
                            : false
                        }
                        inline
                      />
                    }
                  />
                }
              />
            </Tab.Pane>
          ),
        },
        {
          menuItem: (
            <Menu.Item
              key="project.edit.evidence"
              className="tab-item"
              disabled={
                state.isEvaluationPageOpen ||
                (!Boolean(hasObjectives) &&
                  !isParentProgramActivity(currentActivity.activityPlanTypeId))
              }
            >
              <CustomItem
                currentTab={state.currentTab}
                activity_step_id="Evidence"
                activityPlanTypeId={currentActivity.activityPlanTypeId}
                statusList={completionProgress}
                statusId={statusId}
                title={t('common.evidence').toLocaleUpperCase()}
                popupState={get(props, 'user.options.tips')}
                linkedActivities={currentActivity.linkedActivities}
                popupContentObject={{
                  data: helpTips,
                  key: '12',
                }}
                currentActivity={currentActivity}
              />
            </Menu.Item>
          ),
          render: () => (
            <Tab.Pane
              attached={false}
              className="custom-tab-act"
              loading={activity.actGetLoading}
            >
              <ActivitiesContainer
                next={() => nextTab()}
                previous={() => previousTab()}
                currentTab={state.currentTab}
                tabKey={ACTIVITY_TAB_KEYS.EVIDENCE}
                title={t('common.evidence').toLocaleUpperCase()}
                children={
                  isParentProgramActivity(
                    currentActivity.activityPlanTypeId
                  ) ? (
                    <CustomSegment
                      className="evidence-about-object"
                      title={getEvidenceTitle(currentActivity)}
                      children={
                        <ActivityParentEvidence
                          currentState="project.edit.evidence"
                          {...props}
                        />
                      }
                    />
                  ) : (
                    <>
                      <CustomSegment
                        className="evidence-goals-overview-segment"
                        title={t(
                          'activity.activity_steps.activity_goals_overview'
                        )}
                        children={
                          <ActivityGoalsOverview
                            currentState="project.edit.evidence"
                            {...props}
                          />
                        }
                      />
                      <CustomSegment
                        className="evidence-about-object"
                        title={getEvidenceTitle(currentActivity)}
                        children={
                          <ActivityEvidence
                            currentState="project.edit.evidence"
                            {...props}
                          />
                        }
                      />
                    </>
                  )
                }
              />
            </Tab.Pane>
          ),
        },
        {
          menuItem: (
            <Menu.Item
              key="project.edit.theoryOfChange"
              className="tab-item"
              disabled={
                state.isEvaluationPageOpen ||
                (!Boolean(hasObjectives) &&
                  !isParentProgramActivity(currentActivity.activityPlanTypeId))
              }
            >
              <CustomItem
                currentTab={state.currentTab}
                activityPlanTypeId={currentActivity.activityPlanTypeId}
                parentId={currentActivity.parentId}
                isLinkedActivity={currentActivity.isLinkedActivity}
                statusList={completionProgress}
                statusId={statusId}
                activity_step_id="Theory of Change"
                title={t('common.theory_of_change').toLocaleUpperCase()}
                popupState={get(props, 'user.options.tips')}
                linkedActivities={currentActivity.linkedActivities}
                popupContentObject={{
                  data: helpTips,
                  key: '13',
                }}
                currentActivity={currentActivity}
              />
            </Menu.Item>
          ),
          render: () => (
            <Tab.Pane
              attached={false}
              className="custom-tab-act"
              loading={activity.actGetLoading}
            >
              <ActivitiesContainer
                next={() => nextTab()}
                previous={() => previousTab()}
                currentTab={state.currentTab}
                tabKey={ACTIVITY_TAB_KEYS.THEORY_OF_CHANGE}
                title={t('common.theory_of_change')}
                children={
                  isParentProgramActivity(
                    currentActivity.activityPlanTypeId
                  ) ? (
                    <ActivityParentTheory />
                  ) : (
                    <ActivityTheory />
                  )
                }
              />
            </Tab.Pane>
          ),
        },
        {
          menuItem: (
            <Menu.Item
              key="project.edit.activityPlan"
              className="tab-item"
              disabled={state.isEvaluationPageOpen}
            >
              <CustomItem
                currentTab={state.currentTab}
                activityPlanTypeId={currentActivity.activityPlanTypeId}
                isLinkedActivity={currentActivity.isLinkedActivity}
                statusList={completionProgress}
                statusId={statusId}
                linkedActivities={currentActivity.linkedActivities}
                activity_step_id="Activity Plan"
                title={t('common.activity_plan').toLocaleUpperCase()}
                popupState={get(props, 'user.options.tips')}
                popupContentObject={{
                  data: helpTips,
                  key: '14',
                }}
                currentActivity={currentActivity}
              />
            </Menu.Item>
          ),
          render: () => (
            <Tab.Pane
              attached={false}
              className="custom-tab-act"
              loading={activity.actGetLoading}
            >
              <ActivitiesContainer
                next={() => nextTab()}
                previous={() => previousTab()}
                currentTab={state.currentTab}
                tabKey={ACTIVITY_TAB_KEYS.ACTIVITY_PLAN}
                title={t('common.activity_plan').toLocaleUpperCase()}
                children={<ActivityPlan {...props} />}
              />
            </Tab.Pane>
          ),
        },
        {
          menuItem: (
            <Menu.Item
              key="project.edit.evaluationPlan"
              className="tab-item"
              disabled={!Boolean(hasObjectives)}
            >
              <CustomItem
                currentTab={state.currentTab}
                activityPlanTypeId={currentActivity.activityPlanTypeId}
                isLinkedActivity={currentActivity.isLinkedActivity}
                statusList={completionProgress}
                activity_step_id="Evaluation"
                statusId={statusId}
                title={t('common.evaluation').toLocaleUpperCase()}
                linkedActivities={currentActivity.linkedActivities}
                popupState={get(props, 'user.options.tips')}
                popupContentObject={{
                  data: helpTips,
                  key: isParentProgramActivity(
                    currentActivity.activityPlanTypeId
                  )
                    ? '115'
                    : '15',
                }}
                currentActivity={currentActivity}
              />
            </Menu.Item>
          ),
          render: () => (
            <Tab.Pane
              attached={false}
              className="custom-tab-act"
              loading={activity.actGetLoading}
            >
              <ActivitiesContainer
                next={() => nextTab()}
                previous={() => previousTab()}
                currentTab={state.currentTab}
                tabKey={ACTIVITY_TAB_KEYS.EVALUATION}
                title={t('common.evaluation').toLocaleUpperCase()}
                isEvaluationPageOpen={state.isEvaluationPageOpen}
                children={
                  <>
                    {!isParentProgramActivity(
                      currentActivity.activityPlanTypeId
                    ) &&
                      statusId === 1 && (
                        <Fragment>
                          <MainEvalPlan
                            currentState="project.edit.evaluationPlan"
                            isEvaluationPageOpen={state.isEvaluationPageOpen}
                            getEvaluationPage={(value) =>
                              getEvaluationPage(value)
                            }
                            {...props}
                          />
                        </Fragment>
                      )}
                    {!state.isEvaluationPageOpen && (
                      <div className="my-4">
                        <GreenLightComponent
                          currentTab={state.currentTab}
                          loading={activity.actGetLoading}
                          next={() => nextTab()}
                          previous={() => previousTab()}
                          title={t('common.green_light').toUpperCase()}
                          evidenceTypes={evidenceTypes ? evidenceTypes : null}
                          objective={
                            currentActivity.objective
                              ? currentActivity.objective
                              : []
                          }
                          currentState="project.edit.greenLight"
                          // approvalView={state.approvalView}
                          // renderApproved={renderApproved()}
                        />
                      </div>
                    )}
                    {statusId > 1 &&
                      !isParentProgramActivity(
                        currentActivity.activityPlanTypeId
                      ) && <EvaluationMethodology />}
                    {statusId > 1 &&
                      !isParentProgramActivity(
                        currentActivity.activityPlanTypeId
                      ) && (
                        <CustomSegment
                          title={t(
                            'activity.activity_steps.evaluation_plans_title'
                          )}
                          children={<EvaluationPlanSection {...props} />}
                          extraMargin
                        />
                      )}
                    {size(currentActivity.evaluationPlans) > 1 &&
                      statusId > 1 && (
                        <CustomSegment
                          title={t(
                            'activity.activity_steps.outcome_summary_title'
                          )}
                          className="outcome-Summary"
                          children={<OutcomeSummarySections {...props} />}
                          extraMargin
                        />
                      )}
                  </>
                }
              />
            </Tab.Pane>
          ),
        },
        {
          menuItem: (
            <Menu.Item
              key="project.edit.documentation"
              className="tab-item"
              disabled={statusId === 1}
            >
              <CustomItem
                currentTab={state.currentTab}
                activityPlanTypeId={currentActivity.activityPlanTypeId}
                isLinkedActivity={currentActivity.isLinkedActivity}
                statusList={completionProgress}
                statusId={statusId}
                activity_step_id="Documentation"
                title={t('common.documentation').toLocaleUpperCase()}
                popupState={get(props, 'user.options.tips')}
                linkedActivities={currentActivity.linkedActivities}
                popupContentObject={{
                  data: helpTips,
                  key: isParentProgramActivity(
                    currentActivity.activityPlanTypeId
                  )
                    ? '116'
                    : '18',
                }}
                currentActivity={currentActivity}
              />
            </Menu.Item>
          ),
          render: () => (
            <Tab.Pane
              attached={false}
              className="custom-tab-act"
              loading={activity.actGetLoading}
            >
              <ActivitiesContainer
                next={() => nextTab()}
                previous={() => previousTab()}
                currentTab={state.currentTab}
                tabKey={ACTIVITY_TAB_KEYS.DOCUMENTATION}
                title={t('common.documentation').toLocaleUpperCase()}
                children={
                  <Documentation
                    {...props}
                    currentActivity={currentActivity}
                    approvalView={state.approvalView}
                    renderApproved={() => renderApproved()}
                  />
                }
              />
            </Tab.Pane>
          ),
        },
        {
          menuItem: (
            <Menu.Item
              key="project.edit.report"
              /*
               * Note:  activity-report class name used for help tip showing class name (don't change it or remove it)
               */
              className="tab-item activity-report"
              disabled={
                isParentProgramActivity(currentActivity.activityPlanTypeId)
                  ? // isEvaluationPageOpen is added if the user inside the evaluation plan create or update screen to block the other tabs
                    state.isEvaluationPageOpen || !Boolean(hasObjectives)
                  : (currentActivity.objective &&
                        currentActivity.objective.length > 0) ||
                      statusId >= 2
                    ? state.isEvaluationPageOpen
                    : true
              }
            >
              <CustomItem
                currentTab={state.currentTab}
                activityPlanTypeId={currentActivity.activityPlanTypeId}
                isLinkedActivity={currentActivity.isLinkedActivity}
                statusList={completionProgress}
                statusId={statusId}
                hasObjectives={
                  isParentProgramActivity(currentActivity.activityPlanTypeId)
                    ? currentActivity.parentOutcomes &&
                      currentActivity.parentOutcomes.length
                    : currentActivity.objective &&
                      currentActivity.objective.length > 0
                }
                title={t('common.report').toLocaleUpperCase()}
                activity_step_id="Report"
                popupState={get(props, 'user.options.tips')}
                linkedActivities={currentActivity.linkedActivities}
                popupPosition="bottom right"
                popupContentObject={{
                  data: helpTips,
                  key: isParentProgramActivity(
                    currentActivity.activityPlanTypeId
                  )
                    ? '117'
                    : '19',
                }}
                currentActivity={currentActivity}
              />
            </Menu.Item>
          ),
          render: () => (
            <Tab.Pane
              attached={false}
              className="custom-tab-act"
              loading={activity.actGetLoading}
            >
              <ActivitiesContainer
                closable={currentActivity.statusId}
                next={() => nextTab()}
                previous={() => previousTab()}
                currentTab={state.currentTab}
                downloadLink={`/project/${currentActivity.id}/report/export`}
                exportJsonLink={`/activity/${currentActivity.id}/export-data`}
                tabKey={ACTIVITY_TAB_KEYS.REPORT}
                title={t('common.report').toLocaleUpperCase()}
                reportAllImagesLoaded={reportAllImagesLoaded}
                validationOutput={validationOutput.isValid}
                children={
                  <div>
                    {validationOutput.isValid
                      ? null
                      : // NOTE: Some old activities archived before implementing some validations
                        currentActivity.statusId !== 3 && (
                          <div className="report-outstanding-info">
                            <IssuesBox
                              title={t(
                                'activity.activity_steps.outstanding_information_title'
                              )}
                              messages={validationOutput.validationMessages}
                              isInfo={true}
                            />
                          </div>
                        )}

                    <CustomSegment
                      title={t('common.activity')}
                      className="activity"
                      extraMargin
                      children={
                        <ReportActivity
                          name={
                            currentActivity.name ? currentActivity.name : '-'
                          }
                          owner={
                            currentActivity.owner
                              ? currentActivity.owner.name
                                ? currentActivity.owner.name
                                : '-'
                              : '-'
                          }
                          type={
                            isParentProgramActivity(
                              currentActivity.activityPlanTypeId
                            )
                              ? getActivityType(currentActivity).title || '-'
                              : get(currentActivity, 'eventDetail.detail', '-')
                          }
                          periodGreenLight={
                            currentActivity.greenLightNotifyDate
                              ? currentActivity.greenLightNotifyDate
                              : '-'
                          }
                          periodStart={currentActivity.eventStartDate}
                          periodEnd={currentActivity.eventEndDate}
                          goal={
                            currentActivity.goal ? currentActivity.goal : '-'
                          }
                          goalDescription={
                            currentActivity.goalDescription
                              ? currentActivity.goalDescription
                              : '-'
                          }
                          summary={
                            currentActivity.summary
                              ? currentActivity.summary
                              : currentActivity.description
                                ? currentActivity.description
                                : null
                          }
                          activityPlanTypeId={
                            currentActivity.activityPlanTypeId
                          }
                          parentFestivals={currentActivity.parentFestivals}
                          festivalTypes={festivalTypes}
                        />
                      }
                      toggle={false}
                    />
                    {isParentProgramActivity(
                      currentActivity.activityPlanTypeId
                    ) ? (
                      <CustomSegment
                        className="theory-of-change"
                        title={t('common.theory_of_change')}
                        children={
                          <TheoryOfChange
                            grant
                            currentActivity={
                              currentActivity ? currentActivity : {}
                            }
                            knowing={get(
                              currentActivity,
                              'projectTocKnown',
                              '-'
                            )}
                            willDo={get(currentActivity, 'projectTocDo', '-')}
                            description={null}
                            evidenceTypes={evidenceTypes ? evidenceTypes : null}
                            evidence={currentActivity.evidence || []}
                          />
                        }
                        extraMargin
                        toggle={false}
                      />
                    ) : (
                      <Fragment>
                        <CustomSegment
                          title={
                            currentActivity.primaryPolicy
                              ? currentActivity.primaryPolicy?.policyName
                                ? t(
                                    'activity.activity_steps.intended_outcomes_for_policy',
                                    {
                                      policyName:
                                        currentActivity.primaryPolicy
                                          .policyName,
                                    }
                                  )
                                : t(
                                    'activity.activity_steps.objective_intended_outcomes'
                                  )
                              : t(
                                  'activity.activity_steps.objective_intended_outcomes'
                                )
                          }
                          className="objective_"
                          children={
                            <TheoryOfChange
                              aimLabel={
                                currentActivity.objective
                                  ? currentActivity.objective[0]
                                    ? currentActivity.objective[0].outcome
                                      ? currentActivity.objective[0].outcome
                                          .aiming
                                        ? currentActivity.objective[0].outcome
                                            .aiming
                                        : 'Aiming for '
                                      : 'Aiming for '
                                    : 'Aiming for '
                                  : 'Aiming for '
                              }
                              aim={
                                currentActivity.objective
                                  ? currentActivity.objective[0]
                                    ? currentActivity.objective[0].outcome
                                      ? currentActivity.objective[0].outcome
                                          .contentFormat
                                        ? currentActivity.objective[0].outcome
                                            .contentFormat
                                        : '-'
                                      : '-'
                                    : '-'
                                  : '-'
                              }
                              knowingLabel={
                                currentActivity.objective
                                  ? currentActivity.objective[0]
                                    ? currentActivity.objective[0].outcome
                                      ? currentActivity.objective[0].outcome
                                          .knowing
                                        ? currentActivity.objective[0].outcome
                                            .knowing
                                        : ' and knowing '
                                      : ' and knowing '
                                    : ' and knowing '
                                  : ' and knowing '
                              }
                              knowing={
                                currentActivity.objective
                                  ? currentActivity.objective[0]
                                    ? currentActivity.objective[0].tocKnown
                                      ? currentActivity.objective[0].tocKnown
                                      : '-'
                                    : '-'
                                  : '-'
                              }
                              willDo={
                                currentActivity.objective
                                  ? currentActivity.objective[0]
                                    ? currentActivity.objective[0].tocDo
                                      ? currentActivity.objective[0].tocDo
                                      : '-'
                                    : '-'
                                  : '-'
                              }
                              description={
                                currentActivity.objective
                                  ? currentActivity.objective[0]
                                    ? currentActivity.objective[0].outcome
                                      ? currentActivity.objective[0].outcome
                                          .title
                                        ? currentActivity.objective[0].outcome
                                            .title
                                        : '-'
                                      : '-'
                                    : '-'
                                  : '-'
                              }
                              evidenceTypes={
                                evidenceTypes ? evidenceTypes : null
                              }
                              evidence={
                                currentActivity.objective
                                  ? currentActivity.objective[0]
                                    ? currentActivity.objective[0].evidence
                                      ? currentActivity.objective[0].evidence
                                      : []
                                    : []
                                  : []
                              }
                            />
                          }
                          extraMargin
                          toggle={false}
                        />
                        {currentActivity.secondaryPolicyId ? (
                          <CustomSegment
                            title={
                              currentActivity.secondaryPolicy
                                ? currentActivity.secondaryPolicy.policyName
                                  ? `${t('activity.activity_steps.intended_outcomes')} -
                                    ${currentActivity.secondaryPolicy.policyName}`
                                  : t(
                                      'activity.activity_steps.objective_intended_outcomes'
                                    )
                                : t(
                                    'activity.activity_steps.objective_intended_outcomes'
                                  )
                            }
                            className="objective_"
                            children={
                              <TheoryOfChange
                                aimLabel={
                                  currentActivity.objective
                                    ? currentActivity.objective.length > 1
                                      ? currentActivity.objective[1]
                                        ? currentActivity.objective[1].outcome
                                          ? currentActivity.objective[1].outcome
                                              .aiming
                                            ? currentActivity.objective[1]
                                                .outcome.aiming
                                            : 'Aiming for '
                                          : 'Aiming for '
                                        : 'Aiming for '
                                      : 'Aiming for '
                                    : 'Aiming for '
                                }
                                aim={
                                  currentActivity.objective
                                    ? currentActivity.objective.length > 1
                                      ? currentActivity.objective[1]
                                        ? currentActivity.objective[1].outcome
                                          ? currentActivity.objective[1].outcome
                                              .contentFormat
                                            ? currentActivity.objective[1]
                                                .outcome.contentFormat
                                            : '-'
                                          : '-'
                                        : '-'
                                      : '-'
                                    : '-'
                                }
                                knowingLabel={
                                  currentActivity.objective
                                    ? currentActivity.objective.length > 1
                                      ? currentActivity.objective[1]
                                        ? currentActivity.objective[1].outcome
                                          ? currentActivity.objective[1].outcome
                                              .knowing
                                            ? currentActivity.objective[1]
                                                .outcome.knowing
                                            : ' and knowing '
                                          : ' and knowing '
                                        : ' and knowing '
                                      : ' and knowing '
                                    : ' and knowing '
                                }
                                knowing={
                                  currentActivity.objective
                                    ? currentActivity.objective.length > 1
                                      ? currentActivity.objective[1]
                                        ? currentActivity.objective[1].tocKnown
                                          ? currentActivity.objective[1]
                                              .tocKnown
                                          : '-'
                                        : '-'
                                      : '-'
                                    : '-'
                                }
                                willDo={
                                  currentActivity.objective
                                    ? currentActivity.objective.length > 1
                                      ? currentActivity.objective[1]
                                        ? currentActivity.objective[1].tocDo
                                          ? currentActivity.objective[1].tocDo
                                          : '-'
                                        : '-'
                                      : '-'
                                    : '-'
                                }
                                description={
                                  currentActivity.objective
                                    ? currentActivity.objective[1]
                                      ? currentActivity.objective[1].outcome
                                        ? currentActivity.objective[1].outcome
                                            .title
                                          ? currentActivity.objective[1].outcome
                                              .title
                                          : '-'
                                        : '-'
                                      : '-'
                                    : '-'
                                }
                                evidenceTypes={
                                  evidenceTypes ? evidenceTypes : null
                                }
                                evidence={
                                  currentActivity.objective
                                    ? currentActivity.objective[1]
                                      ? currentActivity.objective[1].evidence
                                        ? currentActivity.objective[1].evidence
                                        : []
                                      : []
                                    : []
                                }
                              />
                            }
                            extraMargin
                            toggle={false}
                          />
                        ) : null}
                      </Fragment>
                    )}
                    <CustomSegment
                      className="key-activity-data"
                      title={t(
                        'activity.activity_steps.key_activity_data_title'
                      )}
                      children={
                        <ActualOutput
                          currencyType={currencyType}
                          outputs={
                            currentActivity.eventDetail
                              ? currentActivity.eventDetail.outputFormat
                                ? currentActivity.eventDetail.outputFormat
                                    .format
                                  ? currentActivity.eventDetail.outputFormat
                                      .format
                                  : []
                                : []
                              : []
                          }
                          allData={currentActivity ? currentActivity : {}}
                          volunteers={
                            currentActivity.actualVolunteers
                              ? currentActivity.actualVolunteers
                              : '-'
                          }
                          venue={
                            currentActivity.venues
                              ? currentActivity.venues.length > 0
                                ? currentActivity.venues
                                : []
                              : []
                          }
                        />
                      }
                      extraMargin
                      toggle={false}
                    />
                    {isParentProgramActivity(
                      currentActivity.activityPlanTypeId
                    ) ? (
                      <ParentReportCharts
                        getAllImages={(value) => getAllImages(value)}
                      />
                    ) : (
                      <ActualOutcomes
                        getAllImages={(value) => getAllImages(value)}
                      />
                    )}
                    <CustomSegment
                      className="analysis-and-reflection"
                      title={t(
                        'activity.activity_steps.analysis_and_reflection_title'
                      )}
                      children={
                        <Notes
                          reflection={currentActivity.reflection || ''}
                          isAnalysisField={currentActivity.isAnalysisField}
                          analysis={currentActivity.analysis || ''}
                          evaluationPlans={
                            currentActivity.evaluationPlans || []
                          }
                          isParent={isParentProgramActivity(
                            currentActivity.activityPlanTypeId
                          )}
                        />
                      }
                      extraMargin
                      toggle={false}
                    />
                  </div>
                }
              />
            </Tab.Pane>
          ),
        },
      ];

      if (!isEmpty(member)) {
        panes = remove(panes, (delPane) => {
          if (isString(member.permissions)) {
            member.permissions = JSON.parse(member.permissions);
          }
          const getPermission = find(member.permissions, (permission) => {
            return permission.state === delPane.menuItem.key;
          });
          // No permission row for this tab (e.g. a member permissions template
          // that predates a tab, or is missing an entry) — default to showing
          // the tab rather than crashing the whole activity page.
          if (!getPermission) {
            return true;
          }
          if (getPermission.permission.read) {
            return true;
          }
          return false;
        });
      }

      // check tab order changes and fix (parent activity)
      panes = compact(
        map(completionProgress, (step) => {
          return find(
            panes,
            (tab) =>
              step.section.toLowerCase() ===
              get(
                tab,
                'menuItem.props.children.props.activity_step_id',
                null
              ).toLowerCase()
          );
        })
      );

      return panes;
    } catch (error) {
      console.error('[generateTabs] failed to build activity tabs:', error);

      return [
        {
          menuItem: (
            <Menu.Item
              className="no-print"
              key={t('common.error').toUpperCase()}
              disabled={true}
            />
          ),
          render: () => (
            <Tab.Pane
              attached={false}
              className="custom-tab-act"
              loading={activity.actGetLoading}
            >
              <ActivitiesContainer
                next={() => nextTab()}
                previous={() => previousTab()}
                tabKey={ACTIVITY_TAB_KEYS.ERROR}
                title={t('common.error').toUpperCase()}
                children={
                  <CustomSegment
                    title={t('activity.activity_steps.error_occurred')}
                    children={
                      <EmptyContainer
                        msg={t('activity.activity_steps.contact_admin_msg')}
                      />
                    }
                  />
                }
              />
            </Tab.Pane>
          ),
        },
      ];
    }
  }

  const nextTab = () => {
    const panes = generateTabs();

    const isTabDeactivatedResult = isTabDeactivated(
      'NEXT',
      currentActivity,
      panes,
      currentTab
    );

    const currentKey = panes[currentTab];

    if (!isTabDeactivatedResult) {
      completeGoalTab(currentKey.menuItem.key, currentTab + 1);
    } else {
      return false;
    }
  };

  const previousTab = () => {
    const panes = generateTabs();

    const isTabDeactivatedResult = isTabDeactivated(
      'BACK',
      currentActivity,
      panes,
      currentTab
    );

    if (!isTabDeactivatedResult) {
      changeRoute(currentTab - 1);
      // Assuming '.act-content-layer' is a DOM element
      const actContentLayer = document.querySelector('.act-content-layer');
      if (actContentLayer) {
        actContentLayer.scrollTop = 0;
      }
    } else {
      return false;
    }
  };

  const onTabClick = (data, currentTab) => {
    const current = data.panes[currentTab];
    completeGoalTab(current.menuItem.key, data.activeIndex);
  };

  const completeGoalTab = (key, activeIndex) => {
    const pageDisable = getPageDisable(
      currentActivity,
      user,
      'project.edit.goal'
    );

    if (key === 'project.edit.start') {
      if (
        currentActivity.completionProgress &&
        currentActivity.completionProgress.length &&
        currentActivity.completionProgress[0].status === 0
      ) {
        if (currentActivity.goal !== '' && !currentActivity.secondaryPolicyId) {
          // Check other conditions and update the state accordingly
          setState((state) => ({
            ...state,
            confirmModal: pageDisable ? false : true,
          }));
        } else if (
          currentActivity.goal !== '' &&
          currentActivity.secondaryPolicyId
        ) {
          // Check other conditions and update the state accordingly
          setState((state) => ({
            ...state,
            confirmModal: pageDisable ? false : true,
          }));
        } else {
          changeRoute(activeIndex);
          // Scroll logic here
        }
      } else {
        changeRoute(activeIndex);
        // Scroll logic here
      }
    } else {
      changeRoute(activeIndex);
      // Scroll logic here
    }
  };

  const close = () => {
    setState((prevState) => ({ ...prevState, confirmModal: false }));
  };

  const renderApproved = () => {
    const { approvalMethod, authorisationCode } = state;
    return (
      <div className="approved-content-div-documentation">
        {state.authorisationCode ? (
          <>
            <div className="approval-active-data">
              <label>
                <strong>
                  {currentActivity.codeLabel || authorisationCode}
                </strong>{' '}
                - {currentActivity.ppmsCode}
              </label>
            </div>
            {approvalMethod !== 'allowSelf' ? (
              <div className="approval-active-data">
                <Icon name="check circle" color="green" />
                <label>
                  <strong>
                    {t('activity.activity_steps.approved_by_label')}
                  </strong>{' '}
                  - {currentActivity.approvalUserName}
                </label>
              </div>
            ) : null}
          </>
        ) : approvalMethod !== 'allowSelf' ? (
          <div className="approval-active-data">
            <Icon name="check circle" color="green" size="large" />
            <label>
              <strong>{t('activity.activity_steps.approved_by_label')}</strong>{' '}
              - {currentActivity.approvalUserName}
            </label>
          </div>
        ) : null}
        {currentActivity.referenceNumber ? (
          <div className="approval-active-data-1">
            <label>
              <strong>{currentActivity.referenceCodeLabel}</strong> -{' '}
              {currentActivity.referenceNumber}
            </label>
          </div>
        ) : null}
      </div>
    );
  };

  const onConfirm = (activeIndex) => {
    setState((state) => ({ ...state, confirmModal: false }));
    changeRoute(activeIndex);
    window.scrollTo(0, 0); // Scroll to the top of the page

    const data = {
      completionProgress: currentActivity.completionProgress,
    };

    data.completionProgress[0].status = 2;
    dispatch(putActivityStatus(currentActivity.id, data));
  };

  const getEvaluationPage = (value) => {
    try {
      setState((prevState) => ({
        ...prevState,
        isEvaluationPageOpen: value,
      }));
    } catch (error) {
      console.error(error);
    }
  };

  const getJoyRide = (data) => {
    const { type } = data;

    if (type === 'tour:end') {
      setState((prevState) => ({
        ...prevState,
        infoSteps: false,
      }));

      if (!user.onboardingOption.activityFormTips) {
        // only trigger for new user/ users not toured whole trip
        user.onboardingOption.activityFormTips = true;
        dispatch(setUserOnboardingOption(user));
      }

      dispatch(setVisibleTips(false)); // hide help panel and off the onboarding flow in sidebar
    }
  };

  const { currentTab, confirmModal, infoSteps } = state;

  if (checkPermissionDenied(user, currentActivity)) return <PermissionDenied />;

  let _panes = generateTabs();
  return (
    <div className="steps-container">
      <Tab
        menu={{ secondary: true, pointing: true }}
        panes={_panes}
        className={`${
          isMobile ? 'custom-tabs-mobile-act' : 'custom-tabs1-act act-tab'
        }${
          currentActivity &&
          isParentProgramActivity(currentActivity.activityPlanTypeId)
            ? ' parent-act-tab'
            : ' single-act-tab'
        }`}
        activeIndex={currentTab}
        onTabChange={(e, data) => onTabClick(data, currentTab)}
      />
      <Modal open={confirmModal} size="tiny">
        <Modal.Header className="goal-confirm-modal-header">
          <div className="modal-heading">
            {t('activity.activity_steps.activity_confirmation_modal_title')}
          </div>
          <div className="slim-close-icon" onClick={() => close()}>
            &times;
          </div>
        </Modal.Header>
        <Modal.Content className="goal-confirm-modal-body">
          {t('activity.activity_steps.activity_confirmation_modal_body')}
        </Modal.Content>
        <Modal.Actions className="goal-confirm-modal-actions">
          <div className="model-buttons">
            <Button className="Secondary-Button" onClick={() => close()}>
              {t('actions.cancel')}
            </Button>
            <Button
              positive
              className="Primary-Button"
              onClick={() => onConfirm(currentTab + 1)}
            >
              {t('actions.ok')}
            </Button>
          </div>

          {/* <Button className='goal-cancel-button' onClick={() => close()}>
            Cancel
          </Button>
          <Button
            className='goal-confirm-button'
            onClick={() => onConfirm(activeIndex)}>
            OK
          </Button> */}
        </Modal.Actions>
      </Modal>
      {/* <Confirm
        open={confirmModal}
        onCancel={() => close()}
        onConfirm={() => onConfirm(activeIndex)}
        header={
          <div className='goal-confirm-modal-header'>
            Activity Confirmation
          </div>
        }
        content={
          <div className='goal-confirm-modal-body'>
            You are about to confirm the goal for your activity. From this
            point the goal will be locked and unable to change. Do you want to
            proceed with this activity addressing this goal?
          </div>
        }
        size='tiny'
      /> */}
      <Joyride
        autoStart
        callback={(data) => getJoyRide(data)}
        run={infoSteps}
        steps={steps}
        continuous
        disableOverlayClose={true} // disable overlay close <- need to go whole trip
        disableCloseOnEsc={true} // disable overlay close [ESC btn]<- need to go whole trip
        tooltipComponent={Tooltip}
        beaconComponent={BeaconComponent}
        scrollToFirstStep
        disableScrolling={!isMobile} // disable scroll in web view <- to resolve wrong target at help panel in web view
      />
    </div>
  );
};

// const mapStateToProps = state => {
//   return {
//     ...state.activity,
//     user: state.authentication.user,
//     evidenceTypes: state.extra.evidenceTypes,
//     helpTips: state.extra.activityHelpTips,
//     parentOutcomes: state.extra.outcomes,
//     festivalTypes: state.extra.festivalTypes,
//   };
// };

// const mapDispatchToProps = dispatch => {
//   return bindActionCreators(
//     {
//       getAllActivityData,
//       putActivityStatus,
//       getReports,
//       putActivity,
//       postGroupSelected,
//       getSelectedGroup,
//       updateGroupSelected,
//       deleteGroupSelected,
//       updateProjectInputs,
//       setUserOnboardingOption,
//     },
//     dispatch
//   );
// };

export default ActivitySteps;
